import React, { useState, useEffect, useMemo } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChartPie, faSpinner } from '@fortawesome/free-solid-svg-icons';
import { createPortal } from 'react-dom';
import { usePopper } from 'react-popper';
import { DEBUG } from '../config/debug';
import PropTypes from 'prop-types';
import { useIndustryList } from '../services/api';
import { SECTORS, sectorForIndustry } from '../shared/industrySectorMap';

const SectorsFilter = ({ activeFilters, onApplyFilters }) => {
  if (DEBUG.filters) {
    console.log('SectorsFilter: Initializing with:', {
      activeFilters: activeFilters,
      hasApplyFilters: !!onApplyFilters
    });
  }

  // Company counts per sector, summed from the per-industry counts
  const { industries, isLoading, error } = useIndustryList();
  const sectorCounts = useMemo(() => {
    const counts = Object.fromEntries(SECTORS.map(sector => [sector, 0]));
    industries.forEach(industry => {
      const sector = sectorForIndustry(industry.id);
      if (sector) counts[sector] += industry.count;
    });
    return counts;
  }, [industries]);
  const hasCounts = !isLoading && !error;

  const [isOpen, setIsOpen] = useState(false);
  const [selectedSectors, setSelectedSectors] = useState(activeFilters || []);
  
  const [referenceElement, setReferenceElement] = useState(null);
  const [popperElement, setPopperElement] = useState(null);

  const { styles, attributes } = usePopper(referenceElement, popperElement, {
    placement: 'bottom-start',
    modifiers: [{ name: 'offset', options: { offset: [0, 4] }}],
  });

  useEffect(() => {
    if (DEBUG.filters) {
      console.log('SectorsFilter: activeFilters updated:', activeFilters);
    }
    setSelectedSectors(activeFilters || []);
  }, [activeFilters]);

  const handleSelectAll = () => {
    // Toggle: if all selected, unselect all; otherwise select all
    if (selectedSectors.length === SECTORS.length) {
      setSelectedSectors([]);
    } else {
      setSelectedSectors([...SECTORS]);
    }
  };

  const handleSectorChange = (sector) => {
    setSelectedSectors(prev => 
      prev.includes(sector) 
        ? prev.filter(name => name !== sector)
        : [...prev, sector]
    );
  };

  const handleApply = () => {
    const selectionCount = selectedSectors.length;
    const isSelectAll = selectionCount === SECTORS.length;
    const isNoneSelected = selectionCount === 0;

    if (DEBUG.filters) {
      console.log('SectorsFilter: Applying filters:', {
        sectors: selectedSectors,
        isSelectAll,
        isNoneSelected
      });
    }
    
    onApplyFilters({
      sectors: (isSelectAll || isNoneSelected) ? null : selectedSectors
    });
    setIsOpen(false);
  };

  const handleCancel = () => {
    setSelectedSectors(activeFilters || []);
    setIsOpen(false);
  };

  return (
    <div className="relative inline-block">
      <div
        ref={setReferenceElement}
        onClick={() => setIsOpen(!isOpen)}
        role="button"
        aria-label="Filter by sector"
        className="cursor-pointer p-2 hover:bg-gray-100 rounded-full transition-colors duration-200"
      >
        <FontAwesomeIcon 
          icon={faChartPie} 
          className={`text-lg ${selectedSectors.length > 0 ? 'text-blue-500' : 'text-gray-600'}`}
        />
      </div>

      {isOpen && createPortal(
        <div
          ref={setPopperElement}
          style={styles.popper}
          {...attributes.popper}
          className="w-72 bg-white shadow-lg ring-1 ring-black/5 rounded-lg z-50 divide-y divide-gray-100"
        >
          <div className="px-4 py-3 border-b">
            <div className="flex items-center justify-between">
              <label className="flex items-center gap-3 hover:bg-gray-50 rounded-md p-2 cursor-pointer transition-colors duration-200">
                <div className="flex items-center justify-center w-5 h-5">
                  <input
                    type="checkbox"
                    checked={selectedSectors.length === SECTORS.length}
                    onChange={handleSelectAll}
                    className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 transition-colors duration-200"
                  />
                </div>
                <span className="font-medium text-sm text-gray-700">SELECT ALL</span>
              </label>
              {isLoading && (
                <FontAwesomeIcon icon={faSpinner} className="text-blue-500 animate-spin" />
              )}
            </div>
          </div>

          <div className="max-h-60 overflow-y-auto py-2">
            {SECTORS.map((sector) => (
              <label
                key={sector}
                className="flex items-center gap-3 px-4 py-2 hover:bg-gray-50 cursor-pointer transition-colors duration-200"
              >
                <div className="flex items-center justify-center w-5 h-5">
                  <input
                    type="checkbox"
                    checked={selectedSectors.includes(sector)}
                    onChange={() => handleSectorChange(sector)}
                    className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 transition-colors duration-200"
                  />
                </div>
                <span className="text-sm text-gray-700">{sector}</span>
                {hasCounts && (
                  <span className="text-xs text-gray-400 ml-auto">{sectorCounts[sector]} companies</span>
                )}
              </label>
            ))}
          </div>

          <div className="p-4 bg-gray-50 rounded-b-lg flex justify-between items-center">
            <div className="text-sm text-gray-500 font-medium">
              {(() => {
                if (selectedSectors.length === 0) return 'All Sectors';
                if (selectedSectors.length === SECTORS.length) return 'All Sectors';
                return `${selectedSectors.length} Selected`;
              })()}
            </div>
            <div className="flex gap-2">
              <button
                onClick={handleCancel}
                className="px-3 py-1.5 text-sm text-gray-600 hover:text-gray-800 transition-colors duration-200"
              >
                Cancel
              </button>
              <button
                onClick={handleApply}
                className="px-4 py-1.5 text-sm bg-blue-500 text-white rounded-md hover:bg-blue-600 transition-colors duration-200 font-medium"
              >
                Apply
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

SectorsFilter.propTypes = {
  activeFilters: PropTypes.arrayOf(PropTypes.string),
  onApplyFilters: PropTypes.func.isRequired
};

SectorsFilter.defaultProps = {
  activeFilters: []
};

// onApplyFilters is compared too: it closes over the current user settings, so a stale
// copy would save filters against old preferences.
export default React.memo(SectorsFilter, (prevProps, nextProps) => {
  return JSON.stringify(prevProps.activeFilters) === JSON.stringify(nextProps.activeFilters) &&
    prevProps.onApplyFilters === nextProps.onApplyFilters;
});
